import { useEffect, useRef } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import DuncLanding from "@/components/DuncLanding";
import PrivacyPage from "@/components/PrivacyPage";
import TermsPage from "@/components/TermsPage";
import FlavourPage from "@/components/FlavourPage";
import MomentsPage from "@/components/MomentsPage";
import ContactPage from "@/components/ContactPage";

function useLenis() {
  const lenisRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add("react-ready");
    const shell = document.getElementById("app-shell");
    if (shell) {
      const t = setTimeout(() => shell.remove(), 400);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return lenisRef;
}

// Handles route changes: resets scroll on nav, honours hash after route load.
function RouteEffects() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // let lazy sections mount, then scroll
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const lenisRef = useLenis();

  const scrollTo = (selector) => {
    if (!selector) return;
    const el = document.querySelector(selector);
    if (el) {
      if (lenisRef.current) lenisRef.current.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    } else {
      // Element not on this route — go to home with hash.
      window.location.assign(`/${selector}`);
    }
  };

  return (
    <>
      <RouteEffects />
      <Routes>
        <Route path="/" element={<DuncLanding scrollTo={scrollTo} />} />
        <Route path="/flavours/:slug" element={<FlavourPage scrollTo={scrollTo} />} />
        <Route path="/moments" element={<MomentsPage scrollTo={scrollTo} />} />
        <Route path="/contact" element={<ContactPage scrollTo={scrollTo} />} />
        <Route path="/privacy" element={<PrivacyPage scrollTo={scrollTo} />} />
        <Route path="/terms" element={<TermsPage scrollTo={scrollTo} />} />
        <Route path="*" element={<DuncLanding scrollTo={scrollTo} />} />
      </Routes>
      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#1F1714",
            border: "1px solid rgba(217,164,65,0.25)",
            color: "#F5EFE6",
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
